import {useState} from "react";
import * as Comlink from 'comlink';
import {PropertyExecutiveSummaryData, SERIES_DATA_OPTION} from "../logic/usePropertyExecutiveSummaryData";
import {ExecutiveSummaryGraphData} from "../logic/seriesGraphDataLogic";
import {Button} from "@zendeskgarden/react-buttons";
import {WorkerType} from './pdfWorker';
import {Inline} from "@zendeskgarden/react-loaders";
import {FaFileDownload} from "react-icons/fa";
import {useAsyncCallback} from 'react-async-hook';
import {saveAs} from 'file-saver';
import {buildExecSummaryMetricData} from "./buildAnalysisPageData";
import {exportCharts} from "./exportToPdf";
import {financialTableDataToRows} from "./buildFinancialTableData";
import {revenueTableDataToRows} from "./buildRevenueTableData";
import {ASSET_LIVING_WORKSPACE_IDS, CUSTOM_T12_TEXT, IClientBrandingAssetBuilt, OPERATIONAL_MENU_OPTIONS} from "../constants";
import {buildExecutiveSummaryStatementNOI, buildExecutiveSummaryStatementRentalIncome, buildExecutiveSummaryStatementRents, buildExecutiveSummaryStatementTotalExpense, buildRentsStatementSummary} from "../logic/summaryStringBuilders";
import {IExecutiveSummaryPDFExportWorkerProps} from './pdfExportHelpers';
import {payrollTableDataToRows} from "./buildPayrollTableData";
// eslint-disable-next-line
import Worker from "worker-loader!./pdfWorker";
import {getMonths} from "../../../utils/months-helper";
import {formatExportTimestampSlug} from "../../../utils/formatters";

export const DownloadButton = (props: { data: PropertyExecutiveSummaryData | undefined, rawChartData: Map<SERIES_DATA_OPTION, ExecutiveSummaryGraphData>, clientBranding: IClientBrandingAssetBuilt, customNarrativeParagraphs: string[] }) => {
    const { data, rawChartData } = props;
    const [isLoading, setIsLoading] = useState(false);
    const exportAction = useExportCallback({
        data,
        rawChartData,
        clientBranding: props.clientBranding,
        customNarrativeParagraphs: props.customNarrativeParagraphs,
    });

    return <Button
                onClick={() => {
                    setIsLoading(true);
                    exportAction.execute().finally(() => setIsLoading(false));
                    }} disabled={data === undefined || isLoading}

    >
        {
            isLoading ?
                <>Generating PDF <Inline size={22} style={{ marginLeft: ".5rem" }} /></>
                : <>Download PDF
                <Button.EndIcon><FaFileDownload /></Button.EndIcon>
                </>
        }
    </Button>;
};

export const useExportCallback = (props: { data: PropertyExecutiveSummaryData | undefined, rawChartData: Map<SERIES_DATA_OPTION, ExecutiveSummaryGraphData>, clientBranding: IClientBrandingAssetBuilt, customNarrativeParagraphs: string[] }) => useAsyncCallback(async () => {
    const { data, rawChartData } = props;

    if (!data) {
        return;
    }

    const worker = new Worker();
    const pdfWorker = Comlink.wrap<WorkerType>(worker);
    // pdfWorker.onProgress(Comlink.proxy((info: string) => console.log(info)));

    const fileName = `${data.property.name} ${data.property.budgetYear} Executive Summary_${formatExportTimestampSlug(new Date())}.pdf`;

    const title_by_series_option = OPERATIONAL_MENU_OPTIONS.toIdMap("value", "label");
    const financialOverviewData = financialTableDataToRows(
            data.property,
            (data.isUsingCustomT12 ? data.customT12FinancialsTable : data.financialsTable) ?? [],
            data.isUsingCustomT12,
            `${CUSTOM_T12_TEXT} (${data.customT12Label})`
    );
    const revenueSummaryData = revenueTableDataToRows(data.property, data?.rawData?.originalRevenueModel);
    const payrollTableData = payrollTableDataToRows(data.employeeTotals);
    const charts = await exportCharts(data.isUsingCustomT12 ? data.customT12GraphSeriesData : data.graphSeriesData, getMonths(props.data?.property));
    const executiveSummaryStatementNOI = buildExecutiveSummaryStatementNOI(data);
    const executiveSummaryStatementRents = buildExecutiveSummaryStatementRents(data);
    const executiveSummaryStatementRentalIncome = buildExecutiveSummaryStatementRentalIncome(data);
    const executiveSummaryStatementTotalExpense = buildExecutiveSummaryStatementTotalExpense(data);
    const fullExecutiveSummaryStatementNOIWithoutExpense = `${executiveSummaryStatementNOI} ${executiveSummaryStatementRents} ${executiveSummaryStatementRentalIncome}`;
    const rentsStatementSummary = buildRentsStatementSummary(data);
    const execSummaryMetricData = buildExecSummaryMetricData(data);
    const clientBranding = props.clientBranding;

    const dataProps: IExecutiveSummaryPDFExportWorkerProps = {
        data,
        customT12Text: CUSTOM_T12_TEXT,
        rawChartData,
        financialOverviewData,
        revenueSummaryData,
        payrollTableData,
        charts,
        title_by_series_option,
        fullExecutiveSummaryStatementNOIWithoutExpense,
        executiveSummaryStatementTotalExpense,
        executiveSummaryStatementNOI,
        customNarrativeParagraphs: props.customNarrativeParagraphs,
        rentsStatementSummary,
        execSummaryMetricData: JSON.stringify(execSummaryMetricData),
        clientBranding,
        assetLivingWorkspaceIds: ASSET_LIVING_WORKSPACE_IDS,
    };

    try {
        const blob = await pdfWorker.generateSingle(dataProps);
        saveAs(blob, fileName);
    } catch (error) {
        console.log("Error generating PDF");
        console.trace(error);
    }
    finally {
        worker.terminate();
    }
});
